import {
  getSuperStats,
  type SuperStatsResponse,
} from '@lms/api-client/backend';
import { toLocalDateString } from '@lms/api-client/dates';
import type { RatingEntry } from '@lms/charts';
import useSWR from 'swr';
import { useLocale } from 'use-intl';

// The Next app fetched an `/api/admin/superstats` route that wrapped
// `getSuperStats` and appended an empty `questions` placeholder (feedback stats
// deferred to a later phase). The SPA calls `getSuperStats` directly, so the
// placeholder is reproduced here to preserve the shape the components expect.
export interface SuperStatsQuestions {
  understanding: RatingEntry[];
  acting: RatingEntry[];
  explaining: RatingEntry[];
}

export interface StatsHookParams {
  period: 'since' | 'month' | 'year' | 'day';
  fromDate?: Date;
}

type HookResponse = Omit<SuperStatsResponse, 'fromDate'> & {
  questions: SuperStatsQuestions;
  fromDate: Date;
  isLoading: boolean;
  error: undefined | Error | string;
};

const emptyQuestions: SuperStatsQuestions = {
  understanding: [],
  acting: [],
  explaining: [],
};

// Ports features/dashboard/use-super-stats.ts. In the Next app this fetched the
// `/api/admin/superstats` route (which proxied `getSuperStats`); the SPA calls
// the backend client directly via SWR.
export function useSuperStats({
  period,
  fromDate,
}: StatsHookParams): HookResponse {
  const locale = useLocale();

  const { data, error, isLoading } = useSWR(
    ['admin/superstats', period, fromDate?.toISOString(), locale],
    () =>
      getSuperStats({
        period,
        fromDate: fromDate ? toLocalDateString(fromDate) : undefined,
      }),
  );

  return {
    questions: emptyQuestions,
    sessions: data?.sessions ?? [],
    fromDate: new Date(data?.fromDate ?? fromDate ?? Date.now()),
    isLoading,
    error,
  };
}
